import { PresignResponse } from './presign';
import type { DestinationMarketplaceValue } from './auction';

/**
 * Per-house enabled features returned by `GET /api/v3/houses/{houseId}`.
 * Must match BE `HouseFeature` enum constant names.
 */
export enum HouseFeature {
    APPROVAL_TIER4 = 'APPROVAL_TIER4',
    BLOCK_INTERNATIONAL_BIDDERS = 'BLOCK_INTERNATIONAL_BIDDERS',
    CROSS_LISTING = 'CROSS_LISTING',
    DOWNLOAD_REGISTRANTS = 'DOWNLOAD_REGISTRANTS',
    WHITE_LABEL = 'WHITE_LABEL',
}

/** @apiRequestSchema EnabledFeaturesApiRequest */
export type EnabledFeaturesApiRequest = {
    enabledFeatures: HouseFeature[];
};

/** @apiRequestSchema EnabledDestinationMarketplacesApiRequest */
export type EnabledDestinationMarketplacesApiRequest = {
    enabledDestinationMarketplaces: DestinationMarketplaceValue[];
};

/** @apiSchema EnabledDestinationMarketplacesResponse */
export type EnabledDestinationMarketplacesResponse = {
    enabledDestinationMarketplaces: DestinationMarketplaceValue[];
};

// API response types use subdivision/postalCode
export type Address = {
    address1: string;
    address2?: string;
    city: string;
    country: string;
    postalCode: string;
    subdivision: string;
};

/** @apiSchema HouseResponse */
export type HouseResponse = {
    address: Address;
    description: string;
    email: string;
    enabledDestinationMarketplaces?: DestinationMarketplaceValue[];
    enabledFeatures?: HouseFeature[];
    fax?: string;
    id: string;
    invoiceEmail?: string;
    locale: string;
    /**
     * The backend has always returned this (`House.kt` → `HouseResponse.logoUrl`), but the wire type
     * omitted it, so it only reached consumers because `transformHouseResponse` spreads the whole
     * response. Declared here (WBPR-4539) so reading it typechecks instead of needing a cast.
     */
    logoUrl?: string;
    moreInfoEmail?: string;
    name: string;
    phone: string;
    phone2?: string;
    presignResponse?: PresignResponse;
    registrationEmail?: string;
    timestamp?: string;
    timezone: string;
    websiteUrl: string;
};

// Internal House type uses state/zipCode for consistency with UI
export type HouseAddress = {
    address1: string;
    address2?: string;
    city: string;
    country: string;
    state: string;
    zipCode: string;
};

export type House = {
    address: HouseAddress;
    description: string;
    email: string;
    enabledDestinationMarketplaces?: DestinationMarketplaceValue[];
    enabledFeatures?: HouseFeature[];
    fax?: string;
    id: string;
    invoiceEmail?: string;
    locale: string;
    logoUrl?: string;
    moreInfoEmail?: string;
    name: string;
    phone: string;
    phone2?: string;
    registrationEmail?: string;
    timestamp?: string;
    timezone: string;
    websiteUrl: string;
};

// Mock data for testing (API response format)
export const mockHouseResponse: HouseResponse = {
    address: {
        address1: '10 East 38th Street',
        address2: '4th Floor',
        city: 'New York',
        country: 'USA',
        postalCode: '10016',
        subdivision: 'NY',
    },
    description: 'ATG Seller Portal - Auction Technology Group New York Office',
    email: 'support@atg.com',
    id: 'cef50732-6c34-11e2-a6b0-782bcb22e01f',
    locale: 'en-US',
    name: 'ATG Seller Portal - New York',
    phone: '+1 212-634-0977',
    timezone: 'America/New_York',
    websiteUrl: 'https://www.auctiontechnologygroup.com/',
};

export const mockHouseResponse2: HouseResponse = {
    address: {
        address1: '123 Market Street',
        city: 'San Francisco',
        country: 'USA',
        postalCode: '94103',
        subdivision: 'CA',
    },
    description: 'ATG Seller Portal - Auction Technology Group San Francisco Office',
    email: 'sf-support@atg.com',
    id: 'abc12345-1234-1234-1234-123456789abc',
    locale: 'en-US',
    name: 'ATG Seller Portal - San Francisco',
    phone: '+1 415-555-0100',
    timezone: 'America/Los_Angeles',
    websiteUrl: 'https://www.auctiontechnologygroup.com/sf',
};

// Mock data for testing (internal format with state/zipCode)
export const mockHouse: House = {
    address: {
        address1: '10 East 38th Street',
        address2: '4th Floor',
        city: 'New York',
        country: 'USA',
        state: 'NY',
        zipCode: '10016',
    },
    description: 'ATG Seller Portal - Auction Technology Group New York Office',
    email: 'support@atg.com',
    id: 'cef50732-6c34-11e2-a6b0-782bcb22e01f',
    locale: 'en-US',
    name: 'ATG Seller Portal - New York',
    phone: '+1 212-634-0977',
    timezone: 'America/New_York',
    websiteUrl: 'https://www.auctiontechnologygroup.com/',
};

export const mockHouse2: House = {
    address: {
        address1: '123 Market Street',
        city: 'San Francisco',
        country: 'USA',
        state: 'CA',
        zipCode: '94103',
    },
    description: 'ATG Seller Portal - Auction Technology Group San Francisco Office',
    email: 'sf-support@atg.com',
    id: 'abc12345-1234-1234-1234-123456789abc',
    locale: 'en-US',
    name: 'ATG Seller Portal - San Francisco',
    phone: '+1 415-555-0100',
    timezone: 'America/Los_Angeles',
    websiteUrl: 'https://www.auctiontechnologygroup.com/sf',
};

export const mockHouseList: HouseResponse[] = [mockHouseResponse, mockHouseResponse2];
